// ============================================
// AUTH STORE — Zustand
// User info, token management, logout
// ============================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { TOKEN, REFRESH_TOKEN, USER, TOKEN_EXPIRY, REFRESH_TOKEN_EXPIRY } from '@/utils/constants';

export interface AuthUser {
  _id?: string;
  id?: string;
  uuid?: string;
  firstName: string;
  lastName: string;
  email: string;
  userType?: string;
  type?: string;
  profileImage?: string;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  setAuth: (
    user: AuthUser,
    token: { token: string; expiresAt: string },
    refreshToken?: { token: string; expiresAt: string },
  ) => void;
  setTokens: (
    accessToken: { token: string; expiresAt: string },
    refreshToken: { token: string; expiresAt: string },
  ) => void;
  setToken: (token: { token: string; expiresAt: string }) => void;
  logout: () => void;
  updateUser: (user: Partial<AuthUser>) => void;
}

const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      setAuth: (user, token, refreshToken) => {
        localStorage.setItem(TOKEN, token.token);
        localStorage.setItem(TOKEN_EXPIRY, token.expiresAt);
        if (refreshToken) {
          localStorage.setItem(REFRESH_TOKEN, refreshToken.token);
          localStorage.setItem(REFRESH_TOKEN_EXPIRY, refreshToken.expiresAt);
        }
        localStorage.setItem(USER, JSON.stringify(user));
        set({ user, token: token.token, isAuthenticated: true });
      },

      setTokens: (accessToken, refreshToken) => {
        localStorage.setItem(TOKEN, accessToken.token);
        localStorage.setItem(TOKEN_EXPIRY, accessToken.expiresAt);
        localStorage.setItem(REFRESH_TOKEN, refreshToken.token);
        localStorage.setItem(REFRESH_TOKEN_EXPIRY, refreshToken.expiresAt);
        set({ token: accessToken.token });
      },

      setToken: (token) => {
        localStorage.setItem(TOKEN, token.token);
        localStorage.setItem(TOKEN_EXPIRY, token.expiresAt);
        set({ token: token.token });
      },

      logout: () => {
        localStorage.removeItem(TOKEN);
        localStorage.removeItem(TOKEN_EXPIRY);
        localStorage.removeItem(REFRESH_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN_EXPIRY);
        localStorage.removeItem(USER);
        set({ user: null, token: null, isAuthenticated: false });
      },

      updateUser: (partialUser) => {
        set((state) => {
          const updated = state.user ? { ...state.user, ...partialUser } : null;
          if (updated) localStorage.setItem(USER, JSON.stringify(updated));
          return { user: updated };
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

export default useAuthStore;
